import * as React from 'react';
import { cn } from '@utils/cn';

/**
 * Page Layout Context for managing sidebar state
 */
interface PageLayoutContextValue {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const PageLayoutContext = React.createContext<PageLayoutContextValue | undefined>(undefined);

/**
 * Hook to access PageLayout context
 */
export const usePageLayout = () => {
  const context = React.useContext(PageLayoutContext);
  if (!context) {
    throw new Error('usePageLayout must be used within a PageLayout');
  }
  return context;
};

/**
 * PageLayout Root Component
 */
export interface PageLayoutProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * PageLayout Component
 *
 * A flexible layout component for building application pages with header, sidebar, main content, and footer.
 *
 * @example
 * <PageLayout>
 *   <PageHeader>
 *     <h1>CDSS Dashboard</h1>
 *   </PageHeader>
 *   <PageSidebar>
 *     <Navigation />
 *   </PageSidebar>
 *   <PageMain>
 *     <p>Main content goes here</p>
 *   </PageMain>
 *   <PageFooter>
 *     <p>© 2025 CDSS</p>
 *   </PageFooter>
 * </PageLayout>
 */
const PageLayout: React.FC<PageLayoutProps> = ({ children, className }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  return (
    <PageLayoutContext.Provider value={{ sidebarOpen, setSidebarOpen }}>
      <div className={cn('min-h-screen bg-background', className)}>{children}</div>
    </PageLayoutContext.Provider>
  );
};
PageLayout.displayName = 'PageLayout';

/**
 * Page Header Component
 */
export interface PageHeaderProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Make header sticky at the top
   */
  sticky?: boolean;
  /**
   * Add border at the bottom
   */
  bordered?: boolean;
}

/**
 * PageHeader Component
 *
 * Header section of the page layout.
 *
 * @example
 * <PageHeader sticky bordered>
 *   <div className="flex items-center justify-between">
 *     <h1>Dashboard</h1>
 *     <UserMenu />
 *   </div>
 * </PageHeader>
 */
const PageHeader = React.forwardRef<HTMLElement, PageHeaderProps>(
  ({ className, sticky, bordered, ...props }, ref) => (
    <header
      ref={ref}
      className={cn(
        'bg-background px-4 py-3 md:px-6',
        sticky && 'sticky top-0 z-40',
        bordered && 'border-b',
        className
      )}
      {...props}
    />
  )
);
PageHeader.displayName = 'PageHeader';

/**
 * Page Sidebar Component
 */
export interface PageSidebarProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Sidebar width
   */
  width?: string;
  /**
   * Collapsible sidebar
   */
  collapsible?: boolean;
}

/**
 * PageSidebar Component
 *
 * Sidebar section of the page layout.
 *
 * @example
 * <PageSidebar width="250px">
 *   <nav>
 *     <NavLink to="/dashboard">Dashboard</NavLink>
 *     <NavLink to="/patients">Patients</NavLink>
 *   </nav>
 * </PageSidebar>
 */
const PageSidebar = React.forwardRef<HTMLElement, PageSidebarProps>(
  ({ className, width = '250px', collapsible = true, ...props }, ref) => {
    const context = usePageLayout();
    const sidebarOpen = collapsible ? context.sidebarOpen : true;

    if (collapsible && !sidebarOpen) return null;

    return (
      <aside
        ref={ref}
        className={cn('border-r bg-card', collapsible && 'transition-all duration-300', className)}
        style={{ width }}
        {...props}
      />
    );
  }
);
PageSidebar.displayName = 'PageSidebar';

/**
 * Page Main Content Component
 */
export interface PageMainProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Maximum content width
   */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /**
   * Center content horizontally
   */
  centered?: boolean;
}

/**
 * PageMain Component
 *
 * Main content section of the page layout.
 *
 * @example
 * <PageMain maxWidth="2xl" centered>
 *   <h2>Page Content</h2>
 *   <p>Main content area</p>
 * </PageMain>
 */
const PageMain = React.forwardRef<HTMLElement, PageMainProps>(
  ({ className, maxWidth = 'full', centered = false, children, ...props }, ref) => {
    const maxWidthClass = {
      sm: 'max-w-screen-sm',
      md: 'max-w-screen-md',
      lg: 'max-w-screen-lg',
      xl: 'max-w-screen-xl',
      '2xl': 'max-w-screen-2xl',
      full: 'max-w-full',
    }[maxWidth];

    return (
      <main ref={ref} className={cn('flex-1 px-4 py-6 md:px-6 md:py-8', className)} {...props}>
        <div className={cn(centered && 'mx-auto', maxWidthClass)}>{children}</div>
      </main>
    );
  }
);
PageMain.displayName = 'PageMain';

/**
 * Page Footer Component
 */
export interface PageFooterProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Add border at the top
   */
  bordered?: boolean;
}

/**
 * PageFooter Component
 *
 * Footer section of the page layout.
 *
 * @example
 * <PageFooter bordered>
 *   <p className="text-sm text-muted-foreground">
 *     © 2025 Clinical Decision Support System
 *   </p>
 * </PageFooter>
 */
const PageFooter = React.forwardRef<HTMLElement, PageFooterProps>(
  ({ className, bordered, ...props }, ref) => (
    <footer
      ref={ref}
      className={cn('bg-background px-4 py-4 md:px-6', bordered && 'border-t', className)}
      {...props}
    />
  )
);
PageFooter.displayName = 'PageFooter';

/**
 * Page Container Component (wraps header, sidebar, and main)
 */
export type PageContainerProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * PageContainer Component
 *
 * Container for sidebar and main content with flex layout.
 *
 * @example
 * <PageLayout>
 *   <PageHeader />
 *   <PageContainer>
 *     <PageSidebar />
 *     <PageMain />
 *   </PageContainer>
 *   <PageFooter />
 * </PageLayout>
 */
const PageContainer = React.forwardRef<HTMLDivElement, PageContainerProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-1', className)} {...props} />
  )
);
PageContainer.displayName = 'PageContainer';

export { PageLayout, PageHeader, PageSidebar, PageMain, PageFooter, PageContainer };
